/**
 * Swiping between buildings on the home screen.
 *
 * Every building can be paged to, one not reached yet as a dark shape to look
 * at (amending ADR-0019), so a swipe follows the arrows: toward a building the
 * page turns, past the first or the last it stretches like a rubber band and
 * springs back. Swipe left for the next building, right for the one before, as
 * pages turn.
 *
 * The rules are plain functions, tested; `attachSwipe` only wires them to
 * pointer events. Vertical scrolling stays the browser's: the surface is
 * `touch-action: pan-y`, and a gesture that starts vertical is never taken.
 */

export type Page = 'prev' | 'next';

/** How far a drag must go, as a share of the width, to turn the page. */
export const TURN_SHARE = 0.22;
/** Or how fast a flick must be, in px per ms, if it has gone a little way. */
export const FLICK_SPEED = 0.45;
export const FLICK_MIN_PX = 24;
/** Toward nowhere, a drag moves this much of the finger: a stretch, not a page. */
export const RESIST = 0.28;
/**
 * Movement before a gesture picks its axis, in px. A tap wobbles a little, and
 * a sideways drag swallows the tap, so this sits above a thumb's jitter.
 */
export const LOCK_PX = 12;

/** Which way a gesture is going, once it has gone far enough to tell. */
export function lockAxis(dx: number, dy: number): 'x' | 'y' | null {
  if (Math.abs(dx) < LOCK_PX && Math.abs(dy) < LOCK_PX) return null;
  return Math.abs(dx) > Math.abs(dy) * 1.2 ? 'x' : 'y';
}

/** Where the building sits under a finger that has moved `dx`. */
export function dragOffset(dx: number, canPrev: boolean, canNext: boolean): number {
  const open = dx > 0 ? canPrev : canNext;
  return open ? dx : dx * RESIST;
}

/** On release: turn a page, or settle back. */
export function swipeOutcome(
  dx: number,
  speed: number,
  width: number,
  canPrev: boolean,
  canNext: boolean,
): Page | null {
  if (dx === 0) return null;
  const page: Page = dx < 0 ? 'next' : 'prev';
  if (!(page === 'next' ? canNext : canPrev)) return null;
  const far = Math.abs(dx) >= width * TURN_SHARE;
  const flick =
    Math.abs(dx) >= FLICK_MIN_PX &&
    Math.abs(speed) >= FLICK_SPEED &&
    Math.sign(speed) === Math.sign(dx);
  return far || flick ? page : null;
}

export interface SwipeOptions {
  /** Read at the moment of each gesture, so a page that changes can update them. */
  canPrev: boolean;
  canNext: boolean;
  /** What follows the finger: the building in front. */
  target: () => HTMLElement | null;
  /** The finger has moved the building this far (for the street to follow). */
  drag?: (offset: number) => void;
  /** Released short: the building goes back. */
  settle?: () => void;
  /** Released far enough: the page is turning. */
  commit?: (page: Page) => void;
  /** The building is off the edge: show the next one. */
  turn: (page: Page) => void;
}

function reducedMotion(): boolean {
  return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Turn horizontal drags on `surface` into page turns. Attached once; the
 * options object is read live, so the page it serves can change underneath.
 */
export function attachSwipe(surface: HTMLElement, options: SwipeOptions): void {
  let start: { x: number; y: number; id: number } | null = null;
  let axis: 'x' | 'y' | null = null;
  let last = { x: 0, t: 0 };
  let speed = 0;
  let dragged = false;

  const width = (): number => surface.clientWidth || 1;
  const place = (offset: number, animate: boolean): void => {
    const moving = options.target();
    if (!moving) return;
    moving.style.transition = animate ? 'transform 0.2s ease-out' : 'none';
    moving.style.transform = offset === 0 ? '' : `translateX(${offset}px)`;
  };

  surface.addEventListener('pointerdown', (e) => {
    if (!e.isPrimary || (e.pointerType === 'mouse' && e.button !== 0)) return;
    start = { x: e.clientX, y: e.clientY, id: e.pointerId };
    axis = null;
    dragged = false;
    speed = 0;
    last = { x: e.clientX, t: e.timeStamp };
  });

  surface.addEventListener('pointermove', (e) => {
    if (!start || e.pointerId !== start.id) return;
    const dx = e.clientX - start.x;
    if (axis === null) {
      axis = lockAxis(dx, e.clientY - start.y);
      if (axis !== 'x') return;
      surface.setPointerCapture(e.pointerId);
      dragged = true;
    }
    if (axis !== 'x') return;
    const dt = e.timeStamp - last.t;
    if (dt > 0) speed = (e.clientX - last.x) / dt;
    last = { x: e.clientX, t: e.timeStamp };
    const offset = dragOffset(dx, options.canPrev, options.canNext);
    place(offset, false);
    options.drag?.(offset);
  });

  const end = (e: PointerEvent): void => {
    if (!start || e.pointerId !== start.id) return;
    const dx = e.clientX - start.x;
    const horizontal = axis === 'x';
    start = null;
    axis = null;
    if (!horizontal) return;
    const page =
      e.type === 'pointerup'
        ? swipeOutcome(dx, speed, width(), options.canPrev, options.canNext)
        : null;
    if (!page) {
      place(0, true);
      options.settle?.();
      return;
    }
    options.commit?.(page);
    if (reducedMotion()) {
      options.turn(page);
      return;
    }
    // Off the edge it goes, then the next building slides in from the other.
    let turned = false;
    const turn = (): void => {
      if (turned) return;
      turned = true;
      options.turn(page);
    };
    const moving = options.target();
    if (!moving) {
      turn();
      return;
    }
    moving.style.transition = 'transform 0.16s ease-in';
    moving.style.transform = `translateX(${page === 'next' ? -width() : width()}px)`;
    moving.addEventListener('transitionend', turn, { once: true });
    setTimeout(turn, 220);
  };
  surface.addEventListener('pointerup', end);
  surface.addEventListener('pointercancel', end);

  // A drag is not a tap: the floor under the finger must not open a shift.
  surface.addEventListener(
    'click',
    (e) => {
      if (!dragged) return;
      e.preventDefault();
      e.stopPropagation();
      dragged = false;
    },
    true,
  );
}
